import type { ReactNode } from 'react';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { UI_TIMING } from '@heglo/ui/tokens';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

type FieldHelpProps = {
  field: string;
  helpText: ReactNode;
  focusable?: boolean;
};

export function FieldHelp({ field, helpText, focusable = true }: FieldHelpProps) {
  const { t } = useTranslation();

  return (
    <TooltipProvider delayDuration={UI_TIMING.tooltipDelayMs}>
      <Tooltip>
        <TooltipTrigger asChild>
          {focusable ? (
            <button
              type="button"
              aria-label={t('common.fieldHelpAria', { field })}
              className="inline-flex rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <Info className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          ) : (
            <span className="inline-flex rounded-full text-muted-foreground" aria-hidden="true">
              <Info className="h-3.5 w-3.5" />
            </span>
          )}
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="z-50 max-w-xs rounded-md bg-popover px-3 py-2 text-xs leading-relaxed text-popover-foreground shadow-md"
        >
          {helpText}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
